import React, { useState, useRef, useEffect, useContext } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,  
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { putClient } from "../API/clientAPI";
import { putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import LocationCard from "./FormComponents/LocationCard";
import StepHeaderCard from "./FormComponents/StepHeaderCard";
import StepActionBar from "./FormComponents/StepActionBar";
import PageOneReviewSection from "./FormComponents/PageOneReviewSection";
import PaymentDetailsSection from "./FormComponents/PaymentDetailsSection";
import TechnicianSignatureCard from "./FormComponents/TechnicianSignatureCard";
import EditTechniciansDialog from "./FormComponents/EditTechniciansDialog";
import ClientSignatureCard from "./FormComponents/ClientSignatureCard";
import ClientSatisfactionCard from "./FormComponents/ClientSatisfactionCard";
import DocumentLinksCard from "./FormComponents/DocumentLinksCard";
import { buildServiceReportEmail } from "./FormComponents/email/buildServiceReportEmail";
import { buildGeneratedDocumentLinks } from "./FormComponents/email/buildGeneratedDocumentLinks";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

// Import S3 configurations and bucket names
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

// Pest Count for LocationCard
const PEST_OPTIONS = ["Roaches","Mosq","Rodents","Flies","Ants","Others"];

function MakatiGpcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    inspection_report: [],
    services: [],
    chemical_used: [],
    equipment_used: [],
    service_fee: "",
    service_report_payment_status: [],
    service_report_payment_method: [],
    job_date: "",
    start_time: "",
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});

  const [editedClient, setEditedClient] = useState();

  const [links, setLinks] = useState({});

  const [currentStep, setCurrentStep] = useState(0);

  const getGeneratedLinks = (jobOrderId) =>
    buildGeneratedDocumentLinks(jobOrderId, {
      includeReport: true,
      includeCertificate: true,
      includeReceipt: true,
    });
    
  const resolvedLinks = links || {};

  const reportGenerated = Boolean(
    links?.receipts_url ||
    links?.certificate_url ||
    links?.reports_url
  );
  
  const STEP_INDEX = {
    REVIEW_JOB_DETAILS: 0,
    INSPECTION_REPORT: 1,
    SERVICE_DETAILS: 2,
    PAYMENT_DETAILS: 3,
    TECHNICIAN_ONLY: 4,
    CLIENT_SIGNATURE: 5,
    CLIENT_SATISFACTION: 6,
    FINAL_REVIEW: 7,
  };

  const handleCancel = () => {
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate("/manage-service-reports");
    }
  };

  const handleClose = () => {
    navigate("/manage-service-reports");
  };

  const goBackToStep = (stepIndex) => {
    setCurrentStep(stepIndex);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");

  const [controlAgainst, setControlAgainst] = useState({
    crawlingInsects: false,
    flyingInsects:   false,
    rodentControl:   false,
    others:          false,
  });
  const [otherControlText, setOtherControlText] = useState("");

  // Normalize pests into a fixed object shape with numeric values
  const normalizePests = (p) => {
    // base with all six keys = 0
    const base = PEST_OPTIONS.reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});

    if (Array.isArray(p)) {
      // OLD shape: [{ type, count }]
      for (const { type, count } of p) {
        if (type && Object.prototype.hasOwnProperty.call(base, type)) {
          base[type] = Number(count) || 0;
        }
      }
    } else if (p && typeof p === "object") {
      // NEW shape: { Roaches: 0, Mosq: 0, ... }
      for (const [k, v] of Object.entries(p)) {
        if (Object.prototype.hasOwnProperty.call(base, k)) {
          base[k] = Number(v) || 0;
        }
      }
    }
    return base;
  };

  // helper to sum pests object safely
  const sumPests = (obj = {}) =>
  PEST_OPTIONS.reduce((s, k) => {
    const v = Number(obj?.[k]);
    return s + (isNaN(v) ? 0 : v);
  }, 0);

  // Schema for a single Location row
  const LocationRowSchema = z.object({
    location: z.string().trim().min(1, "Location is required"),
    // allow a sparse/partial pests object; user only needs ONE pest > 0
    pests: z
      .record(z.string(), z.coerce.number().nonnegative())
      .optional()
      .default({}),
    findings: z.string().optional().transform(v => v ?? ""),
    findings_other: z.string().optional().transform(v => v ?? ""),
    recommendation: z.string().optional().transform(v => v ?? ""),
    recommendation_other: z.string().optional().transform(v => v ?? ""),
  }).superRefine((row, ctx) => {
    // ✅ require ONLY: at least one pest type with count > 0 // updated: techs should be able to put 0 for customers with no pest found
    // if (sumPests(row.pests || {}) <= 0) {
    //   ctx.addIssue({
    //     code: z.ZodIssueCode.custom,
    //     path: ["pests"],
    //     message: "Enter a count for at least one pest type (> 0).",
    // });
    // }
    // findings
    if (!row.findings && !row.findings_other) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["findings"],
        message: "Please select a Finding or specify Other",
      });
    }
    if (row.findings === "Other" && !row.findings_other.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["findings_other"],
        message: "Please specify other findings",
      });
    }
    // recommendation
    if (!row.recommendation && !row.recommendation_other) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recommendation"],
        message: "Please select a Recommendation or specify Other",
      });
    }
    if (row.recommendation === "Other" && !row.recommendation_other.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recommendation_other"],
        message: "Please specify other recommendation",
      });
    }
  });

  const MakatiSchema = z.object({
    satisfaction_rating: z.coerce
      .number({
        required_error: "Please pick a rating (1–5).",
        invalid_type_error: "Please pick a rating (1–5).",
      })
      .min(1, { message: "Please pick a rating (1–5)." })
      .max(5, { message: "Please pick a rating (1–5)." }),
    technician_signatory: z.string().trim().min(1, "Please choose a technician signatory."),
    acknowledgedClientSignatory: z.string().trim().min(1, "Please enter the client representative."),
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    acknowledgedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Accomplished signature."),
    status: z
      .string()
      .refine((v) => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
    }),
    control_against_count: z.coerce.number().min(1, "Please select at least one under Control Against."),
    control_against_others: z.coerce.boolean().optional(),
    control_against_others_text: z.string().optional(),
    locations: z.array(LocationRowSchema).min(1, "Add at least one location"),
  }).superRefine((data, ctx) => {
    if (data.control_against_others && !data.control_against_others_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["control_against_others_text"],
        message: 'Please specify "Others" under Control Against.',
      });
    }
  }).passthrough();

  const {
    control,
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(MakatiSchema),
    mode: "onChange",
    defaultValues: {
      satisfaction_rating: 0,
      client_satisfaction_remarks: "",
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      status: "",
      control_against_count: 0,
      control_against_others: false,
      control_against_others_text: "",
      locations: [
        {
          location: "",
          pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
          findings: "",
          findings_other: "",
          recommendation: "",
          recommendation_other: "",
        },
      ],
    },
  });

  const satisfactionRating = useWatch({
    control,
    name: "satisfaction_rating",
  }) || 0;

  const satisfactionRemarks = useWatch({
    control,
    name: "client_satisfaction_remarks",
  }) || "";

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({ control, name: "locations" });

  // watch the entire locations array
  const allLocations = useWatch({ control, name: "locations" }) || [];

  // initialize totals
  const overallTotals = PEST_OPTIONS.reduce((acc, pest) => {
    acc[pest] = 0;
    return acc;
  }, {});

  // sum each location’s pests (supports array OR object)
  allLocations.forEach((loc) => {
    const np = normalizePests(loc?.pests);
    PEST_OPTIONS.forEach((k) => {
      overallTotals[k] += np[k];
    });
  });

  // grand total
  const grandTotal = Object.values(overallTotals).reduce((a, b) => a + b, 0);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        const existingRating =
          Number(data?.satisfaction_rating) >= 1 &&
          Number(data?.satisfaction_rating) <= 5
            ? Number(data.satisfaction_rating)
            : 0;

        setValue("satisfaction_rating", existingRating, {
          shouldValidate: false,
          shouldDirty: false,
        });

        setValue(
          "client_satisfaction_remarks",
          data?.client_satisfaction_remarks || "",
          {
            shouldValidate: false,
            shouldDirty: false,
          }
        );

        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians.map(({ firstname, lastname }) => `${firstname} ${lastname}`);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          inspection_report:  data?.inspection_report || [],
          services:           data?.services          || [],
          pest_problem: data?.pest_problem || "",
          job_date: data?.job_date || "",
          start_time: data?.start_time || "",
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  // for edit tech dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const selectedIds = (jobOrder?.technicians || []).map((tech) => tech.id);
    setEditSelectedTechIds(selectedIds);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => {
    setEditTechsOpen(false);
  };

  const handleEditTechChange = (index, techId) => {
    setEditSelectedTechIds((prev) => {
      const next = [...prev];
      next[index] = techId;
      return next;
    });
  };

  const handleAddEditTech = () => {
    setEditSelectedTechIds((prev) => [...prev, ""]);
  };

  const handleRemoveEditTech = (index) => {
    setEditSelectedTechIds((prev) => prev.filter((_, i) => i !== index));
  };

  const saveEditTechs = async () => {
    const uniqueSelectedIds = [...new Set(editSelectedTechIds.filter(Boolean))];

    const selected = (allTechnicians || [])
      .filter((tech) => uniqueSelectedIds.includes(tech.id))
      .map(({ id, firstname, lastname }) => ({
        id,
        firstname,
        lastname,
      }));

    try {
      setIsLoading(true);

      await putJobOrder(jobOrder.id, { technicians: selected });

      setJobOrder((prev) => ({
        ...prev,
        technicians: selected,
      }));

      setjobOrderParameters((prev) => ({
        ...prev,
        accomplished_by_technician: selected.map(
          (tech) => `${tech.firstname} ${tech.lastname}`
        ),
      }));

      const validNames = new Set(
        selected.map((tech) => `${tech.firstname} ${tech.lastname}`)
      );

      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, {
        shouldValidate: true,
      });

      setSuccessMessage("Technicians updated.");
      setEditTechsOpen(false);
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalid = () => {
    const first = document.querySelector(".MuiFormHelperText-root.Mui-error");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const buildStepLogEntry = (stepKey, stepLabel, timestamp) => ({
    step_key: stepKey,
    step_label: stepLabel,
    action: "NEXT_CLICKED",
    timestamp,
  });

  const logReportStep = async (stepKey, stepLabel) => {
    if (!jobOrder?.id) {
      throw new Error("Missing job order id.");
    }

    const timestamp = new Date().toISOString();

    const existingLogs = Array.isArray(jobOrder?.report_page_logs)
      ? jobOrder.report_page_logs
      : [];

    const nextLogs = [
      ...existingLogs,
      buildStepLogEntry(stepKey, stepLabel, timestamp),
    ];

    const payload = {
      report_page_logs: nextLogs,
      [`${stepKey}_completed_at`]: timestamp,
    };

    await putJobOrder(jobOrder.id, payload);

    setJobOrder((prev) => ({
      ...prev,
      ...payload,
    }));

    return timestamp;
  };

  const goToStep = async ({
    currentStepKey,
    currentStepLabel,
    nextStepIndex,
    fieldsToValidate = [],
  }) => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (fieldsToValidate.length > 0) {
        const isValid = await trigger(fieldsToValidate);

        if (!isValid) {
          setErrorMessage("Please complete the required fields before proceeding.");
          setIsLoading(false);
          onInvalid();
          return;
        }
      }

      await logReportStep(currentStepKey, currentStepLabel);

      setCurrentStep(nextStepIndex);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(`Failed to log step: ${currentStepKey}`, error);
      setErrorMessage("Failed to proceed to the next page. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextFromReviewJobDetails = async () => {
    await goToStep({
      currentStepKey: "review_job_details",
      currentStepLabel: "REVIEW JOB DETAILS",
      nextStepIndex: 1,
      fieldsToValidate: ["status"],
    });
  };

  const handleNextFromInspectionReport = async () => {
    await goToStep({
      currentStepKey: "inspection_report",
      currentStepLabel: "INSPECTION REPORT",
      nextStepIndex: 2,
      fieldsToValidate: ["locations"],
    });
  };

  const handleNextFromServiceDetails = async () => {
    await goToStep({
      currentStepKey: "service_details",
      currentStepLabel: "SERVICE DETAILS",
      nextStepIndex: 3,
      fieldsToValidate: ["control_against_count", "control_against_others_text"],
    });
  };

  const handleNextFromPaymentDetails = async () => {
    await goToStep({
      currentStepKey: "payment_details",
      currentStepLabel: "PAYMENT DETAILS",
      nextStepIndex: 4,
      fieldsToValidate: [],
    });
  };

  const handleNextFromTechnicianOnly = async () => {
    await goToStep({
      currentStepKey: "technician_confirmation",
      currentStepLabel: "FOR TECHNICIANS ONLY",
      nextStepIndex: 5,
      fieldsToValidate: ["technician_signatory", "accomplishedSignature", "accomplishedUploaded"],
    });
  };

  const handleNextFromClientSignature = async () => {
    await goToStep({
      currentStepKey: "client_signature",
      currentStepLabel: "FOR CLIENT ONLY - SIGNATURE",
      nextStepIndex: 6,
      fieldsToValidate: ["acknowledgedClientSignatory", "acknowledgedSignature", "acknowledgedUploaded"],
    });
  };

  const handleNextFromClientSatisfaction = async () => {
    await goToStep({
      currentStepKey: "client_satisfaction",
      currentStepLabel: "FOR CLIENT ONLY - SATISFACTION",
      nextStepIndex: 7,
      fieldsToValidate: ["satisfaction_rating"],
    });
  };

  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));


  const [services, setServices] = useState([
    {
      id: 1,
      activity: "Inspection",
      service: "Initial GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 2,
      activity: "Monitoring",
      service: "Comprehensive GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 3,
      activity: "Spraying",
      service: "Disinfection Treatment",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 4,
      activity: "Misting",
      service: "Exterra Colony Elimination",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 5,
      activity: "Gel Baiting",
      service: "Comprehensive Termite",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 6,
      activity: "Glue Trap",
      service: "Rodent Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 7,
      activity: "Rodent Bait",
      service: "Mosquito Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 8,
      activity: "Rat Cage",
      service: "Cockroach Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 9,
      activity: "Wiping",
      service: "Audit / Follow-up",
      activityChecked: false,
      serviceChecked: false,
    },
  ]);

  const handleServicesCheckboxChange = (id, checkboxType) => {
    setServices((prevServices) =>
      prevServices.map((service) =>
        service.id === id
          ? { ...service, [checkboxType]: !service[checkboxType] }
          : service
      )
    );
  };

  const [chemical_used, setChemicalUsed] = useState([
    { label: "Spraying", chemical: "", dilution: "" },
    { label: "Misting", chemical: "", dilution: "" },
    { label: "Trapping", chemical: "", dilution: "" },
    { label: "Rodent Bait", chemical: "", dilution: "" },
    { label: "Gel Bait", chemical: "", dilution: "" },
    { label: "Others", chemical: "", dilution: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const [equipment_used, setEquipmentUsed] = useState([
    { label: "Sprayer", usage: false },
    { label: "Misting Machine", usage: false },
    { label: "Fogging Machine", usage: false },
    { label: "Power Sprayer", usage: false },
    { label: "Others", usage: false },
  ]);

  const handleEquipmentCheckboxChange = (index) => {
    const newEquipmentUsed = [...equipment_used];
    newEquipmentUsed[index].usage = !newEquipmentUsed[index].usage;
    setEquipmentUsed(newEquipmentUsed);
  };

  const [service_report_payment_status, setServiceReportPaymentStatus] =
    useState([
      { label: "Paid", selection: false },
      { label: "COD", selection: false },
      { label: "For Collection", selection: false },
    ]);

  const [service_report_payment_method, setServiceReportPaymentMethod] = useState([
    { label: "Cash", selection: false },
    { label: "Check", selection: false },
    { label: "Free", selection: false },
  ]);

  // Handler for payment status
  const handlePaymentStatusChange = (statusIndex) => {
    const newStatusOptions = service_report_payment_status.map(
      (status, index) => ({
        ...status,
        selection: index === statusIndex ? !status.selection : false,
      })
    );
    setServiceReportPaymentStatus(newStatusOptions);
  };

  // Handler for payment method
  const handlePaymentMethodChange = (methodIndex) => {
    const newMethodOptions = service_report_payment_method.map((method, index) => ({
      ...method,
      selection: index === methodIndex ? !method.selection : false,
    }));
    setServiceReportPaymentMethod(newMethodOptions);
  };

  const handleControlAgainstChange = (field) => (event) => {
    setControlAgainst(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
    if (field === "others" && !event.target.checked) {
     setOtherControlText("");
     clearErrors("control_against_others_text");
   }
  };

  // count how many controlAgainst are checked
  const controlAgainstCount = React.useMemo(
    () => Object.values(controlAgainst).filter(Boolean).length,
    [controlAgainst]
  );

  // push values into RHF so Zod can validate
  React.useEffect(() => {
    setValue("control_against_count", controlAgainstCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("control_against_others", controlAgainst.others, { shouldValidate: true });
    setValue("control_against_others_text", otherControlText, { shouldValidate: true });
  }, [controlAgainstCount, controlAgainst.others, otherControlText, setValue]);

  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      const url = await resizeAndUploadSignature(accomplishedBySignatureRef, jobOrder.id, "accomplished");
      setAccomplishedSignatureURL(url);
      // ✅ tell RHF that upload succeeded
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });
      clearErrors(["accomplishedSignature", "accomplishedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      console.error("Error saving signature:", error);
      setErrorMessage(`Error saving signature: ${error.message}`);
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAccomplishedSignature = () => {
    accomplishedBySignatureRef.current?.clear();
    setAccomplishedSignatureURL("");
    setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("accomplishedUploaded", false, { shouldValidate: true });
    setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
  };

  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      setIsLoading(true);
      const url = await resizeAndUploadSignature(acknowledgedBySignatureRef, jobOrder.id, "acknowledged");

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      setAcknowledgedSignatureURL(url);
      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });
      clearErrors(["acknowledgedSignature", "acknowledgedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAcknowledgedSignature = () => {
    acknowledgedBySignatureRef.current?.clear();
    setAcknowledgedSignatureURL("");
    setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("acknowledgedUploaded", false, { shouldValidate: true });
    setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  // Function to format date
  // TODO: you can use date-fns for more advanced date formatting
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const handleSendEmail = async (attachmentLinks) => {
    const recipientEmail = jobOrder?.client?.contact?.email?.email;

    if (!recipientEmail) {
      console.warn("⚠️ Email notification skipped - no client email address");
      return;
    }

    const htmlBody = buildServiceReportEmail({
      formattedDate,
      attachmentLinks,
    });

    const lambdaPayload = {
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [recipientEmail],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      await sendEmailQueue(lambdaPayload);
      console.log("✅ Email notification sent successfully");
    } catch (error) {
      console.warn("⚠️ Email notification failed (non-critical):", error.message);
    }
  };

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const inspection_report = Array.from({ length: 15 }).map((_, idx) => {
      const loc = allLocations[idx] || {
        location: "",
        pests: {}, // <- default to object now
        findings: "",
        findings_other: "",
        recommendation: "",
        recommendation_other: "",
      };

      const findings = (loc.findings_other?.trim() || loc.findings || "").trim();
      const recommendation = (loc.recommendation_other?.trim() || loc.recommendation || "").trim();

      const pests = normalizePests(loc.pests); // unify shape
      const total = PEST_OPTIONS.reduce((sum, k) => sum + pests[k], 0);

      return {
        location: loc.location || "",
        pests,
        total: String(total),
        findings,
        recommendation,
      };
    });

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      inspection_report,
      services,
      chemical_used,
      equipment_used,
      service_report_payment_status,
      service_report_payment_method,
      satisfaction_rating: formData.satisfaction_rating,
      client_satisfaction_remarks: formData.client_satisfaction_remarks || "",
      accomplishedSignature: formData.accomplishedSignature,
      acknowledgedSignature: formData.acknowledgedSignature,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      control_against: {
        crawling_insects: controlAgainst.crawlingInsects,
        flying_insects:   controlAgainst.flyingInsects,
        rodent_control:   controlAgainst.rodentControl,
        others:           controlAgainst.others,
        others_text:      otherControlText.trim()
      },
      service_report_status: "Done",
      technician_signatory: formData.technician_signatory,
      technicians: jobOrder?.technicians || [],
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      client_signature_uploaded_at:
        clientSignatureUploadedAt || jobOrder?.client_signature_uploaded_at || null,
      report_submitted_at: new Date().toISOString(),
    };

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters, form: formData });
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      const generatedLinks = getGeneratedLinks(jobOrder.id);

      setLinks(generatedLinks);

      await handleSendEmail(generatedLinks);

      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);

      setCurrentStep(STEP_INDEX.FINAL_REVIEW);
      setSuccessMessage("Service Report and documents generated successfully.");
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleFormSubmit(onSubmit, onInvalid)} className="job-order-form">
        {/* PAGE 1 */}
        <Grid container>
          {currentStep === STEP_INDEX.REVIEW_JOB_DETAILS && (
            <>
              <StepHeaderCard
                title="Review Job Details"
                subtitle="Review the job details before proceeding to the inspection report."
              />

              <PageOneReviewSection
                jobOrder={jobOrder}
                client={client}
                selectedJobTypes={selectedJobTypes}
                selectedJobDescriptions={selectedJobDescriptions}
                jobOrderParameters={jobOrderParameters}
                errors={errors}
                onStatusChange={(value) => handleJObOrderChange("status", value)}
                showStatus={true}
              />

              <StepActionBar
                isLoading={isLoading}
                showBack={false}
                showCancel={true}
                showNext={true}
                onCancel={handleCancel}
                onNext={handleNextFromReviewJobDetails}
              />
            </>
          )}
        </Grid>

        {/* PAGE 2 */}
        {currentStep === STEP_INDEX.INSPECTION_REPORT && (
          <>
            <StepHeaderCard
              title="Inspection Report"
              subtitle="Complete the inspection findings before proceeding."
            />
            {/* Inspection & Pest Count (Mobile) */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                      Inspection Report
                    </Typography>
                  }
                  sx={{ pb: 0, px: 2, pt: 2 }}
                />
                <CardContent sx={{ px: 2, pb: 2 }}>
                  <Stack spacing={2}>
                    {locationFields.map((loc, idx) => (
                      <LocationCard
                        key={loc.id}
                        control={control}
                        register={register}
                        index={idx}
                        removeLocation={removeLocation}
                      />
                    ))}

                    <Button
                      variant="contained"
                      disabled={locationFields.length >= 15}
                      fullWidth
                      onClick={() =>
                        appendLocation({
                          location: "",
                          // pests: [{ type: "", count: "" }],
                          pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
                          findings: "",
                          findings_other: "",
                          recommendation: "",
                          recommendation_other: "",
                        })
                      }
                    >
                      Add New Location ({locationFields.length}/15)
                    </Button>

                    {/* Overall Total Pests Section */}
                    <Grid item xs={12}>
                      <Card variant="outlined" sx={{ mt: 2 }}>
                        <CardHeader
                          title={
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                            >
                              OVERALL TOTAL PESTS
                            </Typography>
                          }
                          sx={{ pb: 0, px: 2, pt: 2 }}
                        />
                        <CardContent sx={{ px: 2, pb: 2 }}>
                          {PEST_OPTIONS.map((pest) => (
                            <Typography
                              key={pest}
                              variant="body2"
                              sx={{ display: "flex", justifyContent: "space-between" }}
                            >
                              {pest}
                              <span>{overallTotals[pest] || 0}</span>
                            </Typography>
                          ))}

                          <Typography
                            variant="subtitle1"
                            sx={{ mt: 1, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}
                          >
                            Total
                            <span>{grandTotal}</span>
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Page 2 buttons */}
            <StepActionBar
              isLoading={isLoading}
              showBack={true}
              showCancel={true}
              showNext={true}
              onBack={() => goBackToStep(STEP_INDEX.REVIEW_JOB_DETAILS)}
              onCancel={handleCancel}
              onNext={handleNextFromInspectionReport}
            />
          </>
        )}

        {/* PAGE 3 */}
        {currentStep === STEP_INDEX.SERVICE_DETAILS && (
          <>
            <StepHeaderCard
              title="Service Details"
              subtitle="Record services performed, activities done, reminders, chemicals used, and control targets."
            />  
            {/* Services Performed */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardHeader
                  title={
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                    >
                      Services Performed
                    </Typography>
                  }
                  sx={{ p: 2, pb: 0 }}
                />
                <CardContent sx={{ pt: 1, px: 2 }}>
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {services
                      // grab all service labels
                      .map((s) => s.service)
                      // sort them alphabetically
                      .sort((a, b) => a.localeCompare(b))
                      // render each with its own checkbox
                      .map((service) => {
                        const item = services.find((s) => s.service === service);
                        return (
                          <Box
                            component="li"
                            key={service}
                            sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                          >
                            <Checkbox
                              checked={item.serviceChecked}
                              onChange={() =>
                                handleServicesCheckboxChange(item.id, 'serviceChecked')
                              }
                            />
                            <Typography variant="body2">{service}</Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Activities Done */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                    >
                      Activities Done
                    </Typography>
                  }
                  sx={{ p: 2, pb: 0 }}
                />
                <CardContent sx={{ pt: 1, px: 2 }}>
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                    {services
                      // grab all activity labels
                      .map((s) => s.activity)
                      // sort them alphabetically
                      .sort((a, b) => a.localeCompare(b))
                      // render each with its own checkbox
                      .map((activity) => {
                        const item = services.find((s) => s.activity === activity);
                        return (
                          <Box
                            component="li"
                            key={activity}
                            sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                          >
                            <Checkbox
                              checked={item.activityChecked}
                              onChange={() =>
                                handleServicesCheckboxChange(item.id, 'activityChecked')
                              }
                            />
                            <Typography variant="body2">{activity}</Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Reminder Notes */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                      Reminder Notes
                    </Typography>
                  }
                  sx={{ pb: 0, px: 2, pt: 2 }}
                />
                <CardContent sx={{ px: 2, pb: 2 }}>
                  <TextField
                    label="Reminder Notes"
                    value={jobOrderParameters?.reminder_notes || ""}
                    onChange={e =>
                      handleJObOrderChange("reminder_notes", e.target.value)
                    }
                    fullWidth
                    multiline
                    rows={6}
                    size="small"
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Chemical Used Section */}
            <Grid
              item
              xs={12}
              md={12}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Grid item xs={12}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              align="center"
                              colSpan={2}
                              style={{ fontWeight: "bold" }}
                            >
                              Chemical Used / Batch # / Expiration
                            </TableCell>
                            <TableCell
                              align="center"
                              style={{ fontWeight: "bold" }}
                            >
                              Dilution
                            </TableCell>
                            <TableCell
                              align="center"
                              colSpan={2}
                              style={{ fontWeight: "bold" }}
                            >
                              Equipment Used
                            </TableCell>                          
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {chemical_used.map((chemical, index) => (
                            <TableRow key={index}>
                              <TableCell>{chemical.label}:</TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  value={chemical.chemical}
                                  onChange={(e) =>
                                    handleChemicalInputChange(
                                      index,
                                      "chemical",
                                      e.target.value
                                    )
                                  }
                                  sx={{ minWidth: 100 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  size="small"
                                  value={chemical.dilution}
                                  onChange={(e) =>
                                    handleChemicalInputChange(
                                      index,
                                      "dilution",
                                      e.target.value
                                    )
                                  }
                                  sx={{ minWidth: 50 }}
                                />
                              </TableCell>
                              {index < equipment_used.length ? (
                                <>
                                  <TableCell>
                                    {equipment_used[index].label}
                                  </TableCell>
                                  <TableCell>
                                    <Checkbox
                                      checked={equipment_used[index].usage}
                                      onChange={() =>
                                        handleEquipmentCheckboxChange(index)
                                      }
                                    />
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell></TableCell>
                                  <TableCell></TableCell>
                                </>
                              )}                            
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Control Against Section */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                    >
                      CONTROL AGAINST
                    </Typography>
                  }
                  sx={{ pb: 0, px: 2, pt: 2 }}
                />
                <CardContent sx={{ px: 2, pb: 2 }}>
                  <FormGroup sx={{ width: "100%" }}>
                    <FormControlLabel
                      sx={{ width: "100%" }}
                      control={
                        <Checkbox
                          checked={controlAgainst.crawlingInsects}
                          onChange={handleControlAgainstChange("crawlingInsects")}
                        />
                      }
                      label="Crawling Insects"
                    />

                    <FormControlLabel
                      sx={{ width: "100%" }}
                      control={
                        <Checkbox
                          checked={controlAgainst.flyingInsects}
                          onChange={handleControlAgainstChange("flyingInsects")}
                        />
                      }
                      label="Flying Insects"
                    />

                    <FormControlLabel
                      sx={{ width: "100%" }}
                      control={
                        <Checkbox
                          checked={controlAgainst.rodentControl}
                          onChange={handleControlAgainstChange("rodentControl")}
                        />
                      }
                      label="Rodent Control"
                    />

                    <FormControlLabel
                      sx={{ width: "100%" }}
                      control={
                        <Checkbox
                          checked={controlAgainst.others}
                          onChange={handleControlAgainstChange("others")}
                        />
                      }
                      label="Others"
                    />
                  </FormGroup>

                  {controlAgainst.others && (
                    <TextField
                      label="Please specify"
                      value={otherControlText}
                      onChange={(e) => setOtherControlText(e.target.value)}
                      fullWidth
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </CardContent>
                {errors?.control_against_count && (
                  <FormHelperText error sx={{ mt: 1 }}>
                    {errors.control_against_count.message}
                  </FormHelperText>
                )}

                {controlAgainst.others && errors?.control_against_others_text && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {errors.control_against_others_text.message}
                  </FormHelperText>
                )}
              </Card>
            </Grid>
            <input type="hidden" {...register("control_against_count")} />
            <input type="hidden" {...register("control_against_others")} />
            <input type="hidden" {...register("control_against_others_text")} />

            {/* Page 3 buttons */}
            <StepActionBar
              isLoading={isLoading}
              showBack={true}
              showCancel={true}
              showNext={true}
              onBack={() => goBackToStep(STEP_INDEX.INSPECTION_REPORT)}
              onCancel={handleCancel}
              onNext={handleNextFromServiceDetails}
            />
          </>
        )}

        {/* PAGE 4 */}
        {currentStep === STEP_INDEX.PAYMENT_DETAILS && (
          <>
            <StepHeaderCard
              title="Payment Details"
              subtitle="Complete service fee and payment information."
            />   
            <PaymentDetailsSection
              serviceFee={jobOrderParameters?.service_fee || ""}
              onServiceFeeChange={(value) => handleJObOrderChange("service_fee", value)}
              paymentStatusOptions={service_report_payment_status}
              paymentMethodOptions={service_report_payment_method}
              onPaymentStatusChange={handlePaymentStatusChange}
              onPaymentMethodChange={handlePaymentMethodChange}
              readOnly={false}
            />

            {/* Page 4 buttons */}
            <StepActionBar
              isLoading={isLoading}
              showBack={true}
              showCancel={true}
              showNext={true}
              onBack={() => goBackToStep(STEP_INDEX.SERVICE_DETAILS)}
              onCancel={handleCancel}
              onNext={handleNextFromPaymentDetails}
            />
          </>
        )}
            
        {/* PAGE 5 */}
        {currentStep === STEP_INDEX.TECHNICIAN_ONLY && (
          <>
            <StepHeaderCard
              title="For Technicians Only"
              subtitle="Confirm the technician roster and sign the form."
            />      

            <TechnicianSignatureCard
              accomplishedBySignatureRef={accomplishedBySignatureRef}
              canvasWidth={canvasWidth}
              ackHeight={ackHeight}
              technicianSignatory={technicianSignatory}
              setTechnicianSignatory={setTechnicianSignatory}
              setValue={setValue}
              clearErrors={clearErrors}
              errors={errors}
              jobOrder={jobOrder}
              clearAccomplishedSignature={clearAccomplishedSignature}
              saveAndUploadAccomplishedSignature={saveAndUploadAccomplishedSignature}
              isLoading={isLoading}
              isTechnician={isTechnician}
              technicianSignatureUploadedAt={technicianSignatureUploadedAt}
              register={register}
              openEditTechs={openEditTechs}
              jobDate={jobOrderParameters.job_date}
              startTime={jobOrderParameters.start_time}
              onJobDateChange={(value) => handleJObOrderChange("job_date", value)}
              onStartTimeChange={(value) => handleJObOrderChange("start_time", value)}
              readOnlySchedule={true}
              showSchedule={true}
              allowEditTechnicians={true}
            />
            
            <EditTechniciansDialog
              open={editTechsOpen}
              onClose={closeEditTechs}
              onSave={saveEditTechs}
              editedTechs={editSelectedTechIds}
              techniciansList={allTechnicians}
              onChangeTech={handleEditTechChange}
              onAddTech={handleAddEditTech}
              onRemoveTech={handleRemoveEditTech}
              isLoading={isLoading}
            />

            {/* Page 5 buttons */}
            <StepActionBar
              isLoading={isLoading}
              showBack={true}
              showCancel={true}
              showNext={true}
              onBack={() => goBackToStep(STEP_INDEX.PAYMENT_DETAILS)}
              onCancel={handleCancel}
              onNext={handleNextFromTechnicianOnly}
            />
          </>
        )}

        {/* PAGE 6 */}
        {currentStep === STEP_INDEX.CLIENT_SIGNATURE && (
          <>
            <StepHeaderCard
              title="For Client Only"
              subtitle="Please sign the form and provide your satisfaction rating on the next page."
            />

            <ClientSignatureCard
              acknowledgedClientSignatory={acknowledgedClientSignatory}
              setAcknowledgedClientSignatory={setAcknowledgedClientSignatory}
              setValue={setValue}
              clearErrors={clearErrors}
              errors={errors}
              acknowledgedBySignatureRef={acknowledgedBySignatureRef}
              canvasWidth={canvasWidth}
              ackHeight={ackHeight}
              saveAndUploadAcknowledgedSignature={saveAndUploadAcknowledgedSignature}
              clearAcknowledgedSignature={clearAcknowledgedSignature}
              isLoading={isLoading}
              isTechnician={isTechnician}
              clientSignatureUploadedAt={clientSignatureUploadedAt}
              readOnly={false}
              showUploadActions={true}
            />

            <StepActionBar
              isLoading={isLoading}
              showBack={false}
              showCancel={false}
              showNext={true}
              onBack={() => goBackToStep(STEP_INDEX.TECHNICIAN_ONLY)}
              onCancel={handleCancel}
              onNext={handleNextFromClientSignature}
            />
          </>
        )}

        {/* PAGE 7 */}
        {currentStep === STEP_INDEX.CLIENT_SATISFACTION && (
          <>
            <StepHeaderCard
              title="For Client Only"
              subtitle="Your satisfaction and feedback are important to us."
            />

            <ClientSatisfactionCard
              rating={satisfactionRating}
              remarks={satisfactionRemarks}
              onRatingChange={(value) => {
                setValue("satisfaction_rating", value, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                clearErrors("satisfaction_rating");
              }}
              onRemarksChange={(value) => {
                setValue("client_satisfaction_remarks", value, {
                  shouldValidate: false,
                  shouldDirty: true,
                });
              }}
              ratingError={errors?.satisfaction_rating?.message}
              readOnly={false}
            />

            <StepActionBar
              isLoading={isLoading}
              showBack={false}
              showCancel={false}
              showNext={true}
              onBack={() => goBackToStep(STEP_INDEX.CLIENT_SIGNATURE)}
              onNext={handleNextFromClientSatisfaction}
            />
          </>
        )}

        {/* PAGE 8 */}
        {currentStep === STEP_INDEX.FINAL_REVIEW && (
          <>
            <StepHeaderCard
              title="Final Review & Submit"
              subtitle="Submit the report and review the generated document links."
            />

            <DocumentLinksCard
              title="Generated Documents"
              links={resolvedLinks}
            />

            {/* Page 8 buttons */}
            <StepActionBar
              isLoading={isLoading}
              showBack={false}
              showCancel={!reportGenerated}
              showSubmit={!reportGenerated}
              showClose={reportGenerated}            
              onCancel={handleCancel}
              onClose={handleClose}
            />
          </>
        )}
          
        <Controller name="acknowledgedSignature" control={control} rules={{ required: true }} render={() => null} />
        <Controller name="acknowledgedUploaded"  control={control} rules={{ validate: v => v === true || "Please click Upload for Acknowledged signature." }} render={() => null} />
        <Controller name="accomplishedSignature" control={control} rules={{ required: true }} render={() => null} />
        <Controller name="accomplishedUploaded"  control={control} rules={{ validate: v => v === true || "Please click Upload for Accomplished signature." }} render={() => null} />
        <Controller name="satisfaction_rating"   control={control} rules={{ required: true, validate: v => Number(v) >= 1 || "Please pick a rating (1–5)." }} render={() => null} />
          
      </form>
    </BaseFormWrapper>
  );
}

export default MakatiGpcForm;
